<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

const digits = ref(['', '', '', ''])
const displayDigits = ref(['•', '•', '•', '•'])
let interval: ReturnType<typeof setInterval>
let stopped = false

const autoFillAnimation = () => {
  let step = 0
  interval = setInterval(() => {
    if (step < 4) {
      digits.value[step] = String(Math.floor(Math.random() * 10))
      displayDigits.value[step] = digits.value[step]
      step++
    } else {
      clearInterval(interval)
      if (!stopped) {
        setTimeout(() => {
          if (stopped) return
          step = 0
          digits.value = ['', '', '', '']
          displayDigits.value = ['•', '•', '•', '•']
          autoFillAnimation()
        }, 2500)
      }
    }
  }, 400)
}

onMounted(() => autoFillAnimation())
onUnmounted(() => {
  stopped = true
  clearInterval(interval)
})
</script>

<template>
  <div class="flex flex-col h-full bg-white overflow-hidden">
    <!-- Header -->
    <div class="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-gray-100">
      <div class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
      </div>
      <p class="font-semibold text-base" style="color: #111827">Signature électronique</p>
    </div>

    <div class="px-4 pt-6 flex flex-col items-center gap-6">
      <!-- YouSign-style logo area -->
      <div class="flex flex-col items-center gap-2">
        <div class="w-16 h-16 rounded-2xl flex items-center justify-center" style="background: #eff6ff">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="1.5">
            <rect x="5" y="11" width="14" height="10" rx="2" ry="2"/>
            <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
          </svg>
        </div>
        <p class="font-semibold text-base text-center" style="color: #111827">Confirmez votre identité</p>
        <p class="text-sm text-center" style="color: #6b7280">
          Un code à 4 chiffres a été envoyé au<br>
          <strong style="color: #374151">+33 6 •• •• •• 47</strong>
        </p>
      </div>

      <!-- OTP input -->
      <div class="flex gap-3">
        <div
          v-for="(d, i) in displayDigits"
          :key="i"
          class="w-12 h-14 rounded-xl border-2 flex items-center justify-center text-xl font-bold transition-all duration-200"
          :style="digits[i] ? 'border-color: #2563eb; background: #eff6ff; color: #1d4ed8' : 'border-color: #e5e7eb; color: #9ca3af'"
        >
          {{ d }}
        </div>
      </div>

      <!-- Resend -->
      <p class="text-sm" style="color: #9ca3af">
        Vous n'avez pas reçu le code ?
        <span style="color: #2563eb; font-weight: 500">Renvoyer</span>
      </p>

      <!-- Confirm button -->
      <div
        class="w-full rounded-xl py-3.5 flex items-center justify-center gap-2 transition-all duration-300"
        :style="digits.every(d => d) ? 'background: #2563eb' : 'background: #d1d5db'"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
        <span class="font-semibold text-sm text-white">Valider et signer</span>
      </div>

      <!-- Security note -->
      <div class="flex items-center gap-2 px-4 py-2.5 rounded-xl w-full" style="background: #f8fafc">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        <p class="text-xs" style="color: #9ca3af">Signature sécurisée via YouSign — conforme eIDAS</p>
      </div>
    </div>
  </div>
</template>
